/**
 * Wire format of the realtime hub (realtime-service `GET /v1/stream`, gateway
 * `/api/v1/realtime/stream` — design/07 §9.1, wire schema
 * libs/py-shop-common/shop_common/events/schemas/wire/realtime.change.v1.json):
 *
 * - `retry: 3000` then `event: ready {connectionId, topics, heartbeatSeconds, replayWindowSeconds}`
 * - `event: change` + `id: <jetstream seq>` + `data: {topic, id, action, version, at, entity?, hint?}`
 *   — "something changed", never the data itself (public topics carry no `entity`/`hint`)
 * - `event: resync {reason}` — refetch what the screen shows (`shutdown` = reconnect with the id)
 * - `event: error {code}` — the stream ends (`superseded` / `unauthorized`)
 * - `: ping` every 20 s
 *
 * The per-service streams of ADR-0009 (design/07 §9.2) use the same framing; their adapters
 * (`streams.ts`) turn their events into the same {@link ChangeMessage}.
 */

/** "Something changed" — refetch it from the owning service. */
export interface ChangeMessage {
  /** Topic the message was delivered on (what the subscriber subscribed to). */
  topic: string;
  /** Entity id (order id, variant id …). */
  id: string;
  /** `created`, `status_changed`, `availability_changed`, `updated` … */
  action: string;
  /** Version of the entity after the change (optimistic concurrency) — null when unknown. */
  version: number | null;
  /** ISO time of the change. */
  at: string;
  /** `order`, `payment`, `variant` … — absent on `public:*` topics (use the topic). */
  entity?: string;
  /** Small tokens / ids / booleans (`{from, to, orderId}`) — never money or PII. */
  hint?: Readonly<Record<string, string | boolean>>;
  /**
   * Payload of a per-service stream (ADR-0009: inventory `{variantId, available, quantity,
   * reason}`, inbox `{summary, item}`, tracking snapshot) — absent on hub messages.
   */
  payload?: unknown;
  /** Stream that delivered it: `hub` or an adapter name (`inventory`, `inbox`, `tracking`). */
  source?: string;
}

export interface SseEvent {
  event: string;
  data: string;
  id: string | null;
}

/** Incremental SSE parser (the WHATWG rules we need): feed text chunks, get complete events. */
export class SseParser {
  private buffer = "";
  private event = "message";
  private data: string[] = [];
  private id: string | null = null;

  feed(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const out: SseEvent[] = [];
    let newline = this.buffer.search(/\r\n|\r|\n/);
    while (newline >= 0) {
      // a lone "\r" at the very end may be the first half of "\r\n" — wait for the next chunk
      if (this.buffer[newline] === "\r" && newline === this.buffer.length - 1) {
        break;
      }
      const line = this.buffer.slice(0, newline);
      const width = this.buffer.startsWith("\r\n", newline) ? 2 : 1;
      this.buffer = this.buffer.slice(newline + width);
      if (line === "") {
        if (this.data.length > 0) {
          out.push({ event: this.event, data: this.data.join("\n"), id: this.id });
        }
        this.event = "message";
        this.data = [];
      } else if (!line.startsWith(":")) {
        const colon = line.indexOf(":");
        const field = colon >= 0 ? line.slice(0, colon) : line;
        const value = colon >= 0 ? line.slice(colon + 1).replace(/^ /, "") : "";
        if (field === "event") {
          this.event = value;
        } else if (field === "data") {
          this.data.push(value);
        } else if (field === "id" && !value.includes("\0")) {
          // the last event id persists across events (SSE spec) — it is what we resume from
          this.id = value;
        }
      }
      newline = this.buffer.search(/\r\n|\r|\n/);
    }
    return out;
  }
}

/** JSON object of a `data:` line; anything else → `{}`. */
export function parseControl(data: string): Record<string, unknown> {
  try {
    const value: unknown = JSON.parse(data);
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** `event: change` data → message; anything malformed → null (ignored). */
export function parseChange(data: string): ChangeMessage | null {
  const value = parseControl(data);
  if (
    typeof value.topic !== "string" ||
    typeof value.id !== "string" ||
    typeof value.action !== "string"
  ) {
    return null;
  }
  const hint =
    typeof value.hint === "object" && value.hint !== null
      ? Object.fromEntries(
          Object.entries(value.hint as Record<string, unknown>).filter(
            (entry): entry is [string, string | boolean] =>
              typeof entry[1] === "string" || typeof entry[1] === "boolean",
          ),
        )
      : undefined;
  return {
    topic: value.topic,
    id: value.id,
    action: value.action,
    version: typeof value.version === "number" ? value.version : null,
    at: typeof value.at === "string" ? value.at : new Date().toISOString(),
    ...(typeof value.entity === "string" ? { entity: value.entity } : {}),
    ...(hint ? { hint } : {}),
  };
}

/** Options of {@link runFetchStream}. */
export interface FetchStreamOptions {
  fetch: typeof fetch;
  url: string;
  /** Bearer (null = none). */
  bearer: string | null;
  lastEventId: string | null;
  /** Aborting it ends the stream (and every callback). */
  controller: AbortController;
  /** No byte (not even `: ping`) for this long → {@link FetchStreamOptions.onFail}. */
  stallMs: number;
  /** Not a 2xx stream (401/403/429/503 …). */
  onRefused: (response: Response) => unknown;
  onOpen?: () => void;
  /** `true` = stop reading. */
  onEvent: (event: SseEvent) => boolean;
  /** Network error / stall. */
  onFail: () => void;
  /** The body ended by itself. */
  onEnd: () => void;
}

/**
 * One fetch-based SSE connection (sends `Authorization`, which `EventSource` cannot): open, read
 * events until the body ends, watch for silence. Every callback stops once the controller aborts.
 */
export async function runFetchStream(o: FetchStreamOptions): Promise<void> {
  const { signal } = o.controller;
  const live = () => !signal.aborted;
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  if (o.bearer) {
    headers.Authorization = `Bearer ${o.bearer}`;
  }
  if (o.lastEventId) {
    headers["Last-Event-ID"] = o.lastEventId;
  }
  let response: Response;
  try {
    response = await o.fetch(o.url, {
      headers,
      signal,
      cache: "no-store",
      credentials: "same-origin",
    });
  } catch {
    if (live()) {
      o.onFail();
    }
    return;
  }
  if (!live()) {
    discard(response);
    return;
  }
  if (!response.ok || !response.body) {
    await o.onRefused(response);
    return;
  }
  o.onOpen?.();
  const reader = response.body.getReader();
  let lastByte = Date.now();
  const stall = setInterval(
    () => {
      if (Date.now() - lastByte > o.stallMs) {
        o.onFail();
      }
    },
    Math.max(1000, Math.floor(o.stallMs / 3)),
  );
  const stop = () => {
    clearInterval(stall);
    void reader.cancel().catch(() => undefined);
  };
  signal.addEventListener("abort", stop, { once: true });
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (!live()) {
        return;
      }
      if (done) {
        o.onEnd();
        return;
      }
      lastByte = Date.now();
      for (const event of parser.feed(decoder.decode(value, { stream: true }))) {
        if (o.onEvent(event) || !live()) {
          return;
        }
      }
    }
  } catch {
    if (live()) {
      o.onFail();
    }
  } finally {
    signal.removeEventListener("abort", stop);
    stop();
  }
}

/** `Retry-After` (seconds, ≤ 300) in ms; 0 when absent. */
export function retryAfterMs(response: Response): number {
  const value = Number(response.headers.get("retry-after"));
  return Number.isFinite(value) && value > 0 ? Math.min(value, 300) * 1000 : 0;
}

/** Topics a 403 of the hub names (`error.details[{topic}]`). */
export async function deniedTopics(response: Response): Promise<string[]> {
  try {
    const body = (await response.json()) as { error?: { details?: Array<{ topic?: unknown }> } };
    return (body.error?.details ?? [])
      .map((detail) => detail.topic)
      .filter((topic): topic is string => typeof topic === "string");
  } catch {
    return [];
  }
}

/** Unread bodies keep the request open — cancel them. */
export function discard(response: Response): void {
  if (response.body && !response.bodyUsed) {
    void response.body.cancel().catch(() => undefined);
  }
}
